// Sugalarm — public self-serve "forgot password" request page (cycle 190).
//
// Unauthenticated SPA route at /forgot-password. One email field → POST
// /api/auth/forgot_password → a SINGLE generic confirmation panel that is
// identical whether or not the account exists (anti-enumeration). The emailed
// link points at the existing cycle-95 /reset-password consume page.
//
// Renders on web + mobile; NOT part of the kiosk shell. Self-contained inline
// styling on the theme tokens (mirrors the cycle-95 ResetPasswordScreen) so it
// works before any settings/theme context loads and re-tints under light/dark;
// no hardcoded hex. Lays out cleanly on the narrow mobile viewport
// (width: min(420px, 100%)).

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import * as authApi from "../../api/auth";
import { Logo } from "../../components/ui/Logo";

const wrap: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "var(--bg)",
  color: "var(--ink)",
  fontFamily: "var(--ui-font)",
};
const card: React.CSSProperties = {
  width: "min(420px, 100%)",
  background: "var(--bg-elev)",
  border: "1px solid var(--line)",
  borderRadius: 16,
  padding: "28px 26px",
  boxSizing: "border-box",
};
const label: React.CSSProperties = {
  display: "block",
  fontSize: "var(--type-floor)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--ink-3)",
  marginBottom: 6,
};
const input: React.CSSProperties = {
  width: "100%",
  font: "inherit",
  padding: "11px 13px",
  borderRadius: 9,
  border: "1px solid var(--line-strong)",
  background: "var(--bg)",
  color: "var(--ink)",
  marginBottom: 14,
  boxSizing: "border-box",
};
const btn: React.CSSProperties = {
  width: "100%",
  font: "inherit",
  fontWeight: 700,
  padding: "12px 16px",
  borderRadius: 10,
  border: "1px solid var(--accent)",
  background: "var(--accent)",
  color: "var(--accent-ink, #fff)",
  cursor: "pointer",
};
const linkBtn: React.CSSProperties = {
  display: "inline-block",
  marginTop: 14,
  color: "var(--accent)",
  background: "none",
  border: "none",
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
  padding: 0,
};

// Cycle 453.2: when a signed-in session opens this page it renders INSIDE
// the alarm shell (the frame, its No Data banner and the off-Now alarm
// control), so it fills the space the frame gives it instead of the whole
// viewport. Layout only — the form, its requests and its navigation are
// unchanged.
const fitWrap: React.CSSProperties = {
  ...wrap,
  minHeight: 0,
  flex: "1 1 auto",
  overflowY: "auto",
};

export function ForgotPasswordScreen({ fitFrame = false }: { fitFrame?: boolean } = {}): JSX.Element {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    // Anti-enumeration: a non-2xx (e.g. 422 malformed) and a 200 both land on
    // the SAME generic confirmation — the UI never reveals account state. The
    // only thing that keeps the user on the form is leaving it blank.
    await authApi.requestPasswordReset(email.trim());
    setBusy(false);
    setSent(true);
  };

  return (
    <div style={fitFrame ? fitWrap : wrap}>
      <div style={card}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <Logo size={40} />
          <span style={{ fontWeight: 700, fontSize: 18 }}>Sugar Alarm</span>
        </div>

        {sent ? (
          <div>
            <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>Check your inbox</h2>
            <p style={{ color: "var(--ink-2)", lineHeight: 1.5 }}>
              If an account exists for that email, a reset link is on its way —
              check your inbox (and spam). The link expires soon and can be
              used once.
            </p>
            <button style={btn} onClick={() => nav("/login")}>
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>
              Reset your password
            </h2>
            <p
              style={{
                color: "var(--ink-3)",
                fontSize: 13,
                lineHeight: 1.5,
                margin: "0 0 18px",
              }}
            >
              Enter the email for your Sugar Alarm account and we'll send a link to
              set a new password.
            </p>

            <label style={label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              style={input}
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@home.com"
            />

            <button style={btn} type="submit" disabled={busy || !email.trim()}>
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <button type="button" style={linkBtn} onClick={() => nav("/login")}>
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
